import { BoxV2 as Box, Flex } from 'portal-commons';
import { FunctionComponent, ReactNode } from 'react';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface FormSectionProps {
  icon?: IconDefinition;
  title?: string;
  message?: string;
  action?: ReactNode;
  error?: string;
  sx?: React.CSSProperties;
}

const Container: FunctionComponent<{ sx?: React.CSSProperties }> = ({
  sx,
  children,
}) => (
  <Flex
    sx={{
      flexDirection: 'column',
      gap: 'l',
      width: '100%',
      paddingTop: 'xl',
      paddingRight: 'xxl',
      paddingBottom: 'xxl',
      paddingLeft: 'xxl',
      border: '1px solid #C0CCD4',
      background: '#FFF',
      boxSizing: 'border-box',
      ...sx,
    }}
  >
    {children}
  </Flex>
);

const HeaderContainer: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      position: 'relative',
      width: '100%',
      flexDirection: 'column',
      gap: 'xs',
    }}
  >
    {children}
  </Flex>
);

const TitleContainer: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 'xs',
      width: '100%',
      paddingTop: 'xxs',
      paddingBottom: 'xxs',
      color: '#4D5659',
    }}
  >
    {children}
  </Flex>
);

const ActionContainer: FunctionComponent = ({ children }) => (
  <Box sx={{ position: 'absolute', top: 0, right: 0 }}>{children}</Box>
);

const Icon: FunctionComponent = ({ children }) => (
  <Box sx={{ fontWeight: 400, fontSize: 'H600' }}>{children}</Box>
);

const Title: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      flex: 1,
      fontWeight: 600,
      fontSize: 'H500',
      lineHeight: 'normal',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

const Message: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      width: '100%',
      color: '#333E41',
      fontWeight: 400,
      fontSize: 'H300',
    }}
  >
    {children}
  </Box>
);

const FormSection: FunctionComponent<FormSectionProps> = ({
  icon,
  title,
  message,
  action,
  error,
  sx,
  children,
}) => {
  return (
    <Container sx={sx}>
      {(icon || title || message) && (
        <HeaderContainer>
          {(icon || title) && (
            <TitleContainer>
              {icon && (
                <Icon>
                  <FontAwesomeIcon icon={icon} />
                </Icon>
              )}
              {title && <Title>{title}</Title>}
            </TitleContainer>
          )}
          {message && (
            <Message>
              {message.split('\\n').map((item, index) => (
                <div key={index}>{item}</div>
              ))}
            </Message>
          )}
          {action && <ActionContainer>{action}</ActionContainer>}
          {error && (
            <Box
              className="error-msg"
              sx={{
                marginTop: '-3px',
                fontWeight: 400,
                fontSize: 'H300',
                color: '#B70505',
              }}
            >
              {error}
            </Box>
          )}
        </HeaderContainer>
      )}
      {children}
    </Container>
  );
};

export default FormSection;
